// MENASA motion layer: editorial text reveals, scroll-linked First Drop images,
// self-drawing orbits and Islamic stars, Vault text entrances, phone depth without live
// blur on weaker phones, and a desktop cursor. Reduced motion and the Theme Editor keep
// every element in its final state, and nothing is hidden unless this script runs.
(() => {
  if (window.MenasaMotion) return;
  const doc = document, root = doc.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const coarse = matchMedia('(pointer: coarse)');
  const still = () => reduced.matches || !!(window.Shopify && window.Shopify.designMode);
  const tier = () => root.dataset.effects || 'full';
  const phone = () => coarse.matches && innerWidth < 1100;
  const clamp = v => Math.max(0, Math.min(1, v));
  const smooth = v => { const t = clamp(v); return t * t * (3 - 2 * t); };
  const ease = (a, b, p) => smooth((p - a) / (b - a));
  const SVGNS = 'http://www.w3.org/2000/svg';

  // ---------- Text splitting ----------
  function splitLetters(el) {
    if (!el || el.querySelector('.mv-mask') || el.children.length) return false;
    const text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return false;
    el.setAttribute('aria-label', text);
    const mask = doc.createElement('span');
    mask.className = 'mv-mask';
    mask.setAttribute('aria-hidden', 'true');
    [...text].forEach((c, i) => {
      const s = doc.createElement('span');
      s.className = 'mv-ch';
      s.style.setProperty('--i', i);
      s.textContent = c === ' ' ? ' ' : c;
      mask.append(s);
    });
    el.replaceChildren(mask);
    return true;
  }
  const lineEls = new Set();
  function splitLines(el, force) {
    if (!el) return false;
    if (el.dataset.mvText == null) {
      if (el.children.length) return false;
      const text = el.textContent.replace(/\s+/g, ' ').trim();
      if (!text) return false;
      el.dataset.mvText = text;
    }
    lineEls.add(el);
    const width = String(el.clientWidth);
    if (!force && el.dataset.mvWidth === width && el.querySelector('.mv-line')) return true;
    el.dataset.mvWidth = width;
    const spans = el.dataset.mvText.split(' ').map(word => {
      const s = doc.createElement('span');
      s.textContent = word;
      return s;
    });
    el.replaceChildren();
    spans.forEach((s, i) => { if (i) el.append(' '); el.append(s); });
    const rows = [];
    let top = null;
    spans.forEach(s => {
      const t = s.offsetTop;
      if (top === null || Math.abs(t - top) > 2) { rows.push([]); top = t; }
      rows[rows.length - 1].push(s.textContent);
    });
    el.replaceChildren(...rows.map((row, i) => {
      const line = doc.createElement('span');
      line.className = 'mv-line';
      const inner = doc.createElement('span');
      inner.className = 'mv-in';
      inner.style.setProperty('--i', i);
      inner.textContent = row.join(' ');
      line.append(inner);
      return line;
    }));
    return true;
  }

  // ---------- Islamic eight-point star (khatam): two squares, an inner star, an
  // octagon, a central star and lozenges at the eight tips, drawn in that order ----------
  const poly = (r, rot, n = 4, cx = 50, cy = 50) => {
    const pts = [];
    for (let k = 0; k < n; k++) {
      const a = (rot + k * 360 / n) * Math.PI / 180;
      pts.push((cx + r * Math.cos(a)).toFixed(2) + ' ' + (cy + r * Math.sin(a)).toFixed(2));
    }
    return 'M' + pts.join('L') + 'Z';
  };
  const KHATAM = [
    [poly(46, 45), 0], [poly(46, 0), .08],
    [poly(29, 22.5), .3], [poly(29, 67.5), .36],
    [poly(19.5, 22.5, 8), .52],
    [poly(8.5, 45), .66], [poly(8.5, 0), .7],
    ...Array.from({ length: 8 }, (_, k) => {
      const a = k * Math.PI / 4;
      return [poly(2.4, k * 45, 4, 50 + 49.6 * Math.cos(a), 50 + 49.6 * Math.sin(a)), .8 + k * .02];
    }),
  ];
  const strokeEls = new Set();
  function sizeStroke(svg) {
    const w = svg.clientWidth || svg.getBoundingClientRect().width;
    if (!w) return;
    const units = svg.viewBox.baseVal.width || 100;
    svg.style.setProperty('--sw', (units / w * (svg.dataset.px || 1)).toFixed(3));
  }
  function khatam(host) {
    let svg = host.querySelector(':scope > .mv-khatam');
    if (!svg) {
      svg = doc.createElementNS(SVGNS, 'svg');
      svg.setAttribute('viewBox', '-4 -4 108 108');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('class', 'mv-khatam mv-khatam--star');
      KHATAM.forEach(([d, delay]) => {
        const path = doc.createElementNS(SVGNS, 'path');
        path.setAttribute('d', d);
        path.setAttribute('pathLength', '1');
        path.style.setProperty('--d', delay);
        svg.append(path);
      });
      host.append(svg);
    }
    svg.dataset.px = '1.2';
    strokeEls.add(svg);
    requestAnimationFrame(() => sizeStroke(svg));
    return svg;
  }

  // ---------- First Drop orbit: a full ring drawn clockwise from the top ----------
  function orbit(piece) {
    const ring = piece.querySelector('.piece__orbit');
    if (!ring) return;
    ring.querySelectorAll('i').forEach((star, k) => star.style.setProperty('--k', k));
    let svg = ring.querySelector('.mv-orbit');
    if (!svg) {
      svg = doc.createElementNS(SVGNS, 'svg');
      svg.setAttribute('class', 'mv-orbit');
      svg.setAttribute('viewBox', '0 0 100 100');
      svg.setAttribute('aria-hidden', 'true');
      const circle = doc.createElementNS(SVGNS, 'circle');
      circle.setAttribute('class', 'mv-orbit__c');
      circle.setAttribute('cx', '50');
      circle.setAttribute('cy', '50');
      circle.setAttribute('r', '49.6');
      circle.setAttribute('pathLength', '1');
      svg.append(circle);
      ring.prepend(svg);
    }
    ring.classList.add('mv-has-orbit');
    strokeEls.add(svg);
    requestAnimationFrame(() => sizeStroke(svg));
  }

  // ---------- First Drop images follow the scroll: they start visible and settle as the
  // piece comes into view, so no scroll speed can outrun them. ----------
  const pieces = new Set(), piecesNear = new Set();
  const pio = new IntersectionObserver(entries => {
    entries.forEach(e => (e.isIntersecting ? piecesNear.add(e.target) : piecesNear.delete(e.target)));
    schedule();
  }, { rootMargin: '10% 0px' });
  function follows(piece) {
    if (pieces.has(piece)) return;
    // The astrolabe owns its piece's entrance (the CSS already turns this effect off there).
    // Following the scroll would restyle the whole instrument, hundreds of SVG nodes, each frame.
    if (piece.querySelector('[data-mnast]')) return;
    pieces.add(piece);
    piece.classList.add('mv-scroll');
    pio.observe(piece);
  }
  // Both cutouts load and decode once the hero has painted, ahead of any fast scroll.
  function preparePieces() {
    doc.querySelectorAll('.menasa-artistic .drop__pieces .piece__image').forEach(img => {
      if (img.dataset.mvReady) return;
      img.dataset.mvReady = '1';
      if (img.loading === 'lazy') img.loading = 'eager';
      if (img.decode) img.decode().catch(() => {});
    });
  }

  // ---------- Scroll-triggered groups: [selector, prepare, visible share needed] ----------
  const rise = (els, d) => els.forEach((el, i) => {
    el.classList.add('mv-rise');
    el.style.setProperty('--i', i);
    if (d != null) el.style.setProperty('--d', d);
  });
  const GROUPS = [
    ['.menasa-artistic .drop__intro', g => {
      const star = g.querySelector('.drop__star');
      if (star) { star.classList.add('mv-has-khatam'); khatam(star); }
      splitLetters(g.querySelector('h2'));
      splitLines(g.querySelector('p'));
    }, .35],
    ['.menasa-artistic .drop__pieces .piece', g => {
      orbit(g);
      follows(g);
      rise([...g.querySelectorAll('.piece__details > *')]);
    }, .2],
    ['.menasa-closing .stay-copy', g => {
      splitLines(g.querySelector('h2'));
      splitLines(g.querySelector('p'));
    }, .6],
    ['.menasa-closing .email-form', g => rise([g]), .6],
    ['.menasa-closing .footer-details', g => rise([...g.children]), .35],
    ['.menasa-closing .footer-wordmark--text', g => splitLetters(g), .5],
    ['.menasa-closing .footer-bottom', g => rise([g]), .6],
    // Other pages: their main headings rise line by line.
    ['#MainContent h1, #MainContent h2', g => {
      if (g.closest('.menasa-artistic, .menasa-closing, .mc-scope, .mch, .mh, .mh-journal, form, [aria-hidden="true"], .visually-hidden')) return false;
      return splitLines(g);
    }, .8],
  ];
  // Film and Vault text enters with the pinned scene's progress and then leaves with the
  // scene's own fades (no separate exits): [selector, in, prepare].
  const JOURNEY = [
    ['.vault-title', .12, g => splitLetters(g)],
    ['.garment-copy--falcon', .27, g => { splitLetters(g.querySelector('h2')); rise([...g.querySelectorAll(':scope > span')], '.4s'); }],
    ['.garment-copy--mosaic', .56, g => { splitLetters(g.querySelector('h2')); rise([...g.querySelectorAll(':scope > span')], '.4s'); }],
  ];

  let io = null, finalAtScan = 0, playedCount = 0;
  const needs = new WeakMap(), playing = new Set();
  function play(el) {
    io && io.unobserve(el);
    if (!el.classList.contains('mv-wait')) return;
    playedCount++;
    el.classList.add('mv-play');
    el.classList.remove('mv-wait');
    playing.add(el);
    setTimeout(() => {
      el.classList.remove('mv-play');
      el.classList.add('mv-done');
      playing.delete(el);
      resplit(el);
    }, 3000);
  }
  // Elements the router is moving right now keep their final state (both use translate).
  const routed = el => el.getAnimations && el.getAnimations({ subtree: true }).some(a => a.id === 'menasa-route');
  function scan() {
    pruneTwins();
    strokeEls.forEach(svg => (svg.isConnected ? sizeStroke(svg) : strokeEls.delete(svg)));
    if (doc.readyState === 'complete') preparePieces();
    if (still()) {
      doc.querySelectorAll('.mv-wait').forEach(el => el.classList.remove('mv-wait'));
      pieces.forEach(piece => { piece.mvQ = null; (piece.querySelector('.piece__image') || piece).style.removeProperty('--mv-q'); });
      journey();
      return;
    }
    io && io.disconnect();
    // Play once enough of the group is on screen, while it is still arriving.
    io = new IntersectionObserver(entries => entries.forEach(e => {
      const need = Math.min(needs.get(e.target) || .4, innerHeight * .5 / Math.max(1, e.boundingClientRect.height));
      if (e.intersectionRatio >= need - .01 || e.boundingClientRect.bottom < 0) play(e.target);
    }), { threshold: [0, .1, .2, .3, .4, .5, .6, .7, .8, .9, 1] });
    const vh = innerHeight;
    // The landing page reveals once per session (layout/theme.liquid): when it has already been seen, it is final.
    const seenHome = root.classList.contains('menasa-home-seen');
    finalAtScan = 0;
    GROUPS.forEach(([sel, prep, need]) => doc.querySelectorAll(sel).forEach(g => {
      if (prep(g) === false) return;
      needs.set(g, need);
      g.classList.remove('mv-play');
      // Anything already on screen (a reload mid-page, Back restoration) is final.
      const r = g.getBoundingClientRect();
      if (r.top < vh * .8 && r.bottom > 0 || r.bottom <= 0 || routed(g) || seenHome && g.closest('.menasa-artistic')) { g.classList.remove('mv-wait'); g.classList.add('mv-done'); finalAtScan++; }
      else { g.classList.remove('mv-done'); g.classList.add('mv-wait'); io.observe(g); }
    }));
    journey();
    buildTwins();
    schedule();
  }

  // ---------- The Vault title reads as lying behind Falcon's fabric. Both share one
  // stacking context and Falcon is already above, but the fabric's top edge fades to
  // transparent exactly where the title sits, so the title showed through it. The title
  // is masked by the fabric's own mask (1 - top fade x side fade), made firmer, so it
  // disappears where the clothing is, without moving anything. ----------
  const FIRM = 1.6;
  let fabricKey = '', fabric = null;
  const alphaOf = c => {
    c = c.trim();
    if (c === 'transparent') return 0;
    const m = c.match(/^rgba?\(([^)]+)\)/);
    if (m) { const v = m[1].split(/[\s,/]+/).filter(Boolean); return v.length > 3 ? parseFloat(v[3]) : 1; }
    if (/^#[0-9a-f]{4}$|^#[0-9a-f]{8}$/i.test(c)) return parseInt(c.length === 5 ? c[4] + c[4] : c.slice(7), 16) / 255;
    return /^#|^black$/i.test(c) ? 1 : NaN;
  };
  // Parses "linear-gradient(to right, rgb(0,0,0) 40%, rgba(0,0,0,0) 99%), ..." into ramps.
  function parseMask(value) {
    const ramps = [];
    const re = /linear-gradient\(((?:[^()]|\([^()]*\))*)\)/g;
    let m;
    while ((m = re.exec(value))) {
      const args = m[1].split(/,(?![^()]*\))/).map(s => s.trim());
      let axis = 'y';
      if (/^to |deg$|turn$|rad$/.test(args[0])) {
        const dir = args.shift();
        if (dir === 'to right') axis = 'x';
        else if (dir !== 'to bottom' && dir !== '180deg') return null;
      }
      const stops = args.map(s => {
        const pos = s.match(/(-?[\d.]+)%\s*$/);
        return { a: alphaOf(pos ? s.slice(0, pos.index) : s), p: pos ? parseFloat(pos[1]) / 100 : null };
      });
      if (stops.length < 2 || stops.some(s => isNaN(s.a))) return null;
      if (stops[0].p == null) stops[0].p = 0;
      if (stops[stops.length - 1].p == null) stops[stops.length - 1].p = 1;
      for (let i = 1; i < stops.length - 1; i++) if (stops[i].p == null) stops[i].p = (stops[i - 1].p + stops[i + 1].p) / 2;
      ramps.push({ axis, stops });
    }
    return ramps.length ? ramps : null;
  }
  // One ramp in the title's own pixels, made firmer (alpha x FIRM, capped at 1). Outside
  // the fabric's box there is no fabric, so the ramp ends there with a hard edge.
  function rampCSS({ axis, stops: given }, start, size) {
    const out = [], first = given[0], last = given[given.length - 1];
    const stops = [
      ...(first.a > 0 ? [{ a: 0, p: Math.min(0, first.p) }, { a: first.a, p: Math.min(0, first.p) }] : []),
      ...given,
      ...(last.a > 0 ? [{ a: last.a, p: Math.max(1, last.p) }, { a: 0, p: Math.max(1, last.p) }] : []),
    ];
    const put = (a, p) => out.push('rgba(0,0,0,' + Math.min(1, a * FIRM).toFixed(3) + ') ' + (start + p * size).toFixed(1) + 'px');
    stops.forEach((s, i) => {
      if (i) {
        const q = stops[i - 1], cross = 1 / FIRM;
        // Where the firmer ramp reaches full strength, add a stop so it stays linear.
        if ((q.a - cross) * (s.a - cross) < 0) put(cross, q.p + (s.p - q.p) * (cross - q.a) / (s.a - q.a));
      }
      put(s.a, s.p);
    });
    return 'linear-gradient(' + (axis === 'x' ? 'to right' : 'to bottom') + ',' + out.join(',') + ')';
  }
  function occlude(title, material, visible) {
    let mask = '';
    if (visible && material) {
      const cs = getComputedStyle(material);
      const raw = cs.maskImage || cs.webkitMaskImage || '';
      const key = raw + innerWidth;
      if (key !== fabricKey) { fabricKey = key; fabric = raw && raw !== 'none' ? parseMask(raw) : null; }
      if (fabric) {
        const t = title.getBoundingClientRect(), f = material.getBoundingClientRect();
        if (f.top < t.bottom && f.bottom > t.top && f.left < t.right && f.right > t.left) {
          const layers = fabric.map(r => (r.axis === 'x' ? rampCSS(r, f.left - t.left, f.width) : rampCSS(r, f.top - t.top, f.height)));
          mask = 'linear-gradient(#000,#000),' + layers.reverse().join(',');
        }
      }
    }
    if (title.dataset.mvMask === mask) return;
    title.dataset.mvMask = mask;
    const n = mask ? (mask.match(/linear-gradient/g) || []).length : 0;
    // Title minus (fabric side fade x fabric top fade).
    const std = n > 2 ? 'subtract, intersect' : 'subtract';
    const wk = n > 2 ? 'source-out, source-in' : 'source-out';
    title.style.webkitMaskImage = mask;
    title.style.webkitMaskComposite = mask ? wk : '';
    title.style.maskImage = mask;
    title.style.maskComposite = mask ? std : '';
  }

  const studies = new WeakSet();
  let journeyUpdate = null;
  function journey() {
    const study = doc.querySelector('#study');
    if (!study) { journeyUpdate = null; return; }
    const act = study.querySelector('.journey');
    const title = study.querySelector('.vault-title');
    const material = study.querySelector('.material--falcon');
    const items = JOURNEY.map(([sel, a, prep]) => [study.querySelector(sel), a, prep]).filter(([el]) => el);
    items.forEach(([el, , prep]) => { prep(el); el.classList.add('mv-journey'); });
    const update = () => {
      const p = act && act.menasaProgress || 0;
      const calm = still();
      items.forEach(([el, a]) => el.classList.toggle('mv-wait', !calm && p < a));
      // The title is fully faded by the scene at .43; nothing to occlude after that.
      if (title) occlude(title, material, !calm && p > .02 && p < .44);
      vaultTwins(study, p);
    };
    journeyUpdate = update;
    update();
    if (!studies.has(study)) { studies.add(study); study.addEventListener('menasa:journey', () => journeyUpdate && journeyUpdate()); }
  }

  // ---------- Phones: Vault depth from pre-softened copies instead of live blur.
  // Two strengths per image (about 4px and 12px) sit above the sharp image, clipped to
  // its silhouette so no softness spreads past a cutout's edge (that read as a glow).
  // Their opacity eases toward the scroll target every frame, so focus changes in either
  // direction look like a focus pull. Computers use it with the lite Vault (data-vault="lite"),
  // including Macs with no blur (11 Oct 2026: without the copies the Vault had no focus pull there
  // and its pictures just sat sharp). The copies are drawn once; scrolling only changes opacity.
  // The hero has no blur on phones (user decision, 29 September 2026): its copies cost eight extra
  // full-screen layers. ----------
  const twins = new Map();
  const idle = window.requestIdleCallback || (fn => setTimeout(fn, 60));
  const canvasFilter = (() => {
    try { const c = doc.createElement('canvas').getContext('2d'); if (!('filter' in c)) return false; c.filter = 'blur(2px)'; return c.filter === 'blur(2px)'; }
    catch { return false; }
  })();
  const VAULT = ['.material--falcon', '.foreground--falcon', '.material--mosaic', '.foreground--mosaic'];
  const vaultTwinsWanted = () => !still() && (phone() ? root.dataset.blur !== 'off' && (tier() === 'light' || tier() === 'balanced') : root.dataset.vault === 'lite');
  function soften(img, src, k, blur) { // src: img, or its bitmap decoded off the main thread
    const nw = img.naturalWidth, nh = img.naturalHeight, disp = Math.max(1, img.offsetWidth);
    const w = Math.max(24, Math.round(Math.min(nw, disp / k))), h = Math.max(24, Math.round(w * nh / nw));
    const c = doc.createElement('canvas');
    c.width = w; c.height = h;
    c.className = 'mv-twin';
    c.setAttribute('aria-hidden', 'true');
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // A sharp base keeps edges from thinning to transparent inside the silhouette.
    ctx.drawImage(src, 0, 0, w, h);
    if (canvasFilter) {
      ctx.filter = 'blur(' + blur + 'px)';
      ctx.drawImage(src, 0, 0, w, h);
      ctx.filter = 'none';
    } else {
      // Without canvas filters, a two-step downscale approximates the same softness.
      const step = (src, sw, sh) => { const t = doc.createElement('canvas'); t.width = Math.max(4, sw); t.height = Math.max(4, sh); const tc = t.getContext('2d'); tc.imageSmoothingQuality = 'high'; tc.drawImage(src, 0, 0, t.width, t.height); return t; };
      const f = Math.max(2, blur * 1.6);
      const small = step(src, Math.round(w / f), Math.round(h / f));
      const mid = step(small, Math.round(w / Math.sqrt(f)), Math.round(h / Math.sqrt(f)));
      ctx.drawImage(mid, 0, 0, w, h);
    }
    // Clip the softness to the sharp image's own alpha: nothing leaks past a cutout.
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(src, 0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
    return { c, E: blur * disp / w };
  }
  function placeTwin(t) {
    const img = t.img, cs = getComputedStyle(img);
    t.layers.forEach(({ c }) => Object.assign(c.style, {
      left: img.offsetLeft + 'px', top: img.offsetTop + 'px',
      width: img.offsetWidth + 'px', height: img.offsetHeight + 'px',
      objectFit: cs.objectFit, objectPosition: cs.objectPosition,
      transform: cs.transform === 'none' ? '' : cs.transform, transformOrigin: cs.transformOrigin,
    }));
  }
  function makeTwin(img) {
    if (!img || twins.has(img)) return;
    const host = img.closest('.scene-layer, .material, .foreground');
    if (!host) return;
    twins.set(img, null);
    // Drawing an <img> into a canvas decodes it on the main thread (hundreds of ms on a phone,
    // mid-scroll for the Vault). The same bytes, from the HTTP cache, decode as a Blob on a worker
    // thread, already scaled to the larger copy's size.
    const decoded = () => {
      const width = Math.max(24, Math.round(Math.min(img.naturalWidth, Math.max(1, img.offsetWidth) / 2)));
      if (!window.createImageBitmap || !img.currentSrc) return Promise.resolve(img);
      return fetch(img.currentSrc, { cache: 'force-cache' })
        .then(r => (r.ok ? r.blob() : Promise.reject()))
        .then(b => createImageBitmap(b, { resizeWidth: width, resizeQuality: 'high' }))
        .catch(() => img);
    };
    const build = () => idle(async () => {
      if (!img.isConnected || !img.naturalWidth || twins.get(img)) return;
      const src = await decoded();
      if (!img.isConnected || twins.get(img)) return;
      const layers = [soften(img, src, 2, 2), soften(img, src, 4, 3)];
      if (src !== img) src.close();
      layers.forEach(({ c }) => host.append(c));
      // Current and target opacity per layer; new twins fade in from zero.
      const t = { img, layers, cur: [0, 0], tgt: [0, 0] };
      twins.set(img, t);
      placeTwin(t);
      schedule();
      journeyUpdate && journeyUpdate();
    });
    if (img.complete && img.naturalWidth) build();
    else img.addEventListener('load', build, { once: true });
  }
  const show = (c, o) => {
    const v = o < .004 ? '0' : o.toFixed(3);
    if (c.style.opacity === v) return;
    c.style.opacity = v;
    c.style.visibility = o < .004 ? 'hidden' : 'visible';
  };
  // The sharp image always stays fully visible underneath; the soft layers stack on top
  // of it. Coverage therefore never exceeds the image's own and never dips.
  function drive(img, blur) {
    const t = twins.get(img);
    if (!t) return;
    const [m, h] = t.layers;
    t.tgt = [clamp(blur / m.E), clamp((blur - m.E) / Math.max(.1, h.E - m.E))];
    if (img.style.opacity) img.style.opacity = '';
    focusPull();
  }
  let pull = 0, pullAt = 0;
  function focusPull() { if (!pull) { pullAt = performance.now(); pull = requestAnimationFrame(pullStep); } }
  function pullStep(now) {
    pull = 0;
    const k = 1 - Math.exp(-Math.min(64, now - pullAt) / 120);
    pullAt = now;
    let moving = false;
    twins.forEach(t => {
      if (!t) return;
      t.layers.forEach((layer, i) => {
        const d = t.tgt[i] - t.cur[i];
        t.cur[i] = Math.abs(d) < .004 ? t.tgt[i] : t.cur[i] + d * k;
        if (t.cur[i] !== t.tgt[i]) moving = true;
        show(layer.c, t.cur[i]);
      });
    });
    if (moving) { pullAt = now; pull = requestAnimationFrame(pullStep); }
  }
  function buildTwins() {
    if (vaultTwinsWanted()) {
      const study = doc.querySelector('#study');
      if (study) VAULT.forEach(sel => makeTwin(study.querySelector(sel + ' img')));
    }
  }
  function pruneTwins() {
    const live = new Set();
    twins.forEach((t, img) => {
      if (img.isConnected && t && t.layers[0].c.isConnected) t.layers.forEach(({ c }) => live.add(c));
      else twins.delete(img);
    });
    doc.querySelectorAll('.mv-twin').forEach(c => { if (!live.has(c)) c.remove(); });
    doc.querySelectorAll('.hero .scene-layer img, #study .material img, #study .foreground img').forEach(img => {
      if (!twins.get(img) && img.style.opacity) img.style.opacity = '';
    });
  }
  function vaultTwins(study, p) {
    if (!vaultTwinsWanted()) return;
    const focus = ease(.53, .72, p), close = ease(.79, .965, p);
    const falcon = focus * 11, mosaic = (1 - focus) * 9 + close * 10;
    const set = (sel, blur) => { const img = study.querySelector(sel + ' img'); img && drive(img, blur); };
    set('.material--falcon', falcon + (1 - ease(.25, .43, p)) * 8);
    set('.foreground--falcon', falcon + (1 - ease(.28, .43, p)) * 10);
    set('.material--mosaic', mosaic);
    set('.foreground--mosaic', mosaic + (1 - ease(.58, .73, p)) * 10);
  }

  // ---------- One frame loop: all reads, then all writes ----------
  let frame = 0;
  function schedule() { if (!frame) frame = requestAnimationFrame(tick); }
  function tick() {
    frame = 0;
    const vh = innerHeight;
    // First Drop: 0 as the image enters, 1 once about a third of it is in view.
    const calm = still();
    const settle = [...piecesNear].map(piece => {
      if (!piece.isConnected) { piecesNear.delete(piece); pieces.delete(piece); pio.unobserve(piece); return null; }
      const r = (piece.querySelector('.piece__visual') || piece).getBoundingClientRect();
      return [piece, calm ? 1 : clamp((vh - r.top) / Math.max(1, r.height * .35))];
    }).filter(Boolean);
    // The last rows of a page can never reach the reveal line; finish them at the end.
    const scroller = doc.scrollingElement || root;
    const late = scrollY + vh >= scroller.scrollHeight - 4
      ? [...doc.querySelectorAll('.mv-wait:not(.mv-journey)')].filter(el => el.getBoundingClientRect().top < vh) : [];
    // Written on the image itself: an inherited value on the piece restyles its whole subtree.
    settle.forEach(([piece, q]) => {
      const v = (1 - Math.pow(1 - q, 3)).toFixed(3);
      if (piece.mvQ !== v) { piece.mvQ = v; (piece.querySelector('.piece__image') || piece).style.setProperty('--mv-q', v); }
    });
    late.forEach(play);
  }
  addEventListener('scroll', schedule, { passive: true });

  // ---------- Desktop magnetic controls ----------
  // The drawn cursor (a mix-blend ring and dot) was removed on 6 Oct 2026: blending a fixed
  // layer over the film and Vault made desktops lag. The system cursor is used everywhere.
  const MAGNETS = '.hero__cta, .piece__details a, .drop__back, .email-row button, .footer-top, .film-toggle, #PrestigeHeader a, #PrestigeHeader button';
  let magnetsOn = false;
  function cursor() {
    if (magnetsOn || !fine.matches || still() || innerWidth < 900) return;
    magnetsOn = true;
    let x = -200, y = -200, raf = 0, magnet = null, box = null;
    const pulls = new Map();
    const loop = () => {
      raf = 0;
      let moving = false;
      pulls.forEach((m, el) => {
        m.x += (m.tx - m.x) * .16; m.y += (m.ty - m.y) * .16;
        const done = Math.abs(m.tx - m.x) + Math.abs(m.ty - m.y) < .05;
        if (done && !m.tx && !m.ty) { el.style.translate = ''; pulls.delete(el); return; }
        el.style.translate = m.x.toFixed(2) + 'px ' + m.y.toFixed(2) + 'px';
        if (!done) moving = true;
      });
      if (moving) raf = requestAnimationFrame(loop);
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(loop); };
    const release = () => {
      if (magnet && pulls.has(magnet)) { const m = pulls.get(magnet); m.tx = 0; m.ty = 0; }
      magnet = null; box = null; kick();
    };
    addEventListener('pointermove', e => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      x = e.clientX; y = e.clientY;
      const t = e.target instanceof Element ? e.target : null;
      const field = !!(t && t.closest('input, textarea, select, [contenteditable="true"]'));
      const m = !field && t ? t.closest(MAGNETS) : null;
      if (m !== magnet) {
        release();
        if (m) { magnet = m; const s = m.style.translate; m.style.translate = ''; box = m.getBoundingClientRect(); m.style.translate = s; }
      }
      if (magnet && box) {
        const dx = x - (box.left + box.width / 2), dy = y - (box.top + box.height / 2);
        const pull = pulls.get(magnet) || { x: 0, y: 0, tx: 0, ty: 0 };
        pull.tx = Math.max(-8, Math.min(8, dx * .22));
        pull.ty = Math.max(-6, Math.min(6, dy * .3));
        pulls.set(magnet, pull);
      }
      if (magnet) kick();
    }, { passive: true });
    addEventListener('scroll', release, { passive: true });
    root.addEventListener('mouseleave', release);
    doc.addEventListener('menasa:page', release);
  }

  // ---------- Lifecycle ----------
  let width = innerWidth, resizeTimer = 0;
  addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      twins.forEach(t => t && t.img.isConnected && placeTwin(t));
      strokeEls.forEach(svg => (svg.isConnected ? sizeStroke(svg) : strokeEls.delete(svg)));
      if (innerWidth === width) return;
      width = innerWidth;
      lineEls.forEach(el => (el.isConnected ? splitLines(el) : lineEls.delete(el)));
    }, 180);
  }, { passive: true });
  // Web fonts can change line breaks. Lines are rebuilt in one task (no visible step), but
  // never while their group is animating; those wait until the reveal has finished.
  const fontsPending = new Set();
  function resplit(group) {
    fontsPending.forEach(el => {
      if (!el.isConnected) { fontsPending.delete(el); return; }
      if (group && !group.contains(el)) return;
      if (el.closest('.mv-play')) return;
      fontsPending.delete(el);
      splitLines(el, true);
    });
  }
  if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(() => { lineEls.forEach(el => fontsPending.add(el)); resplit(); });
  doc.addEventListener('menasa:page', () => requestAnimationFrame(scan));
  doc.addEventListener('menasa:quality', () => { buildTwins(); schedule(); journeyUpdate && journeyUpdate(); });
  doc.addEventListener('shopify:section:load', () => requestAnimationFrame(scan));
  reduced.addEventListener('change', scan);

  // ---------- Diagnostics, only with ?mvdebug=1 in the address ----------
  function debugBadge() {
    let on = false;
    try { on = new URLSearchParams(location.search).has('mvdebug') || sessionStorage.getItem('mvdebug') === '1'; if (on) sessionStorage.setItem('mvdebug', '1'); } catch { /* storage may be blocked */ }
    if (!on || doc.querySelector('.mv-debug')) return;
    const badge = doc.createElement('div');
    badge.className = 'mv-debug';
    badge.setAttribute('aria-hidden', 'true');
    doc.body.append(badge);
    const count = sel => doc.querySelectorAll(sel).length;
    const render = () => {
      badge.textContent = [
        'reduced motion: ' + (reduced.matches ? 'ON' : 'off'),
        'editor: ' + (window.Shopify && window.Shopify.designMode ? 'yes' : 'no'),
        'tier: ' + tier() + '  phone: ' + (phone() ? 'yes' : 'no'),
        'viewport: ' + innerWidth + 'x' + innerHeight,
        'waiting ' + count('.mv-wait') + '  playing ' + count('.mv-play') + '  done ' + count('.mv-done'),
        'final at load: ' + finalAtScan + '  played: ' + playedCount,
      ].join('\n');
    };
    render();
    setInterval(render, 500);
  }

  const start = () => {
    scan();
    cursor();
    debugBadge();
    // Cutouts decode once the page has painted its first screen.
    const early = () => idle(preparePieces);
    if (doc.readyState === 'complete') early(); else addEventListener('load', early, { once: true });
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.MenasaMotion = { refresh: scan };
})();
